import React from 'react';

import classnames from 'classnames';
import _ from 'lodash';

// React components

// Dependencies
import PropTypes from 'prop-types';

// Local components
import { Accent, Text, Type } from '@nerdwallet/currency';
import { Num, Tracking } from '@nerdwallet/nw-client-lib';
import Icon from '@nerdwallet/react-icon';

import getTranslateCSS from '../../../lib/getTranslateCSS';
import DTIDetails from '../dti-details/dti-details';
import Slider from './slider';

// CSS
require('./dti-slider.less');

const { formOpen, formCancel, formInputChanged } = Tracking;
const FORM_NAME = 'dtiSlider';
const DTI_MIN = 20;
const DTI_MAX = 50;
const AFFORDABLE = 36;
const STRETCH = 43;
const AGGRESSIVE = 50;

const splits = [
  {
    value: AFFORDABLE,
    label: 'Affordable',
    scenarioString:
      'This DTI is in the affordable range. You’ll have a comfortable cushion to cover things like food, entertainment and vacations.',
  },
  {
    value: STRETCH,
    label: 'Stretch',
    scenarioString:
      'This DTI is a bit of a stretch. You can afford the mortgage and housing payments, but they might eat into your savings and expenses.',
  },
  {
    value: AGGRESSIVE,
    label: 'Aggressive',
    scenarioString:
      'This DTI is aggressive. People that spend this much on housing and debts are more likely to miss payments when unexpected expenses arise.',
  },
];

class DTISlider extends React.Component {
  constructor(props) {
    super(props);

    _.bindAll(this, [
      'handleChange',
      'onDragEnd',
      'onDragStart',
      'onMouseEnter',
      'onMouseLeave',
      'setLayout',
    ]);

    // Get Initial State is deprecated. Define initial state in the constructor.
    this.state = {
      dragging: false,
      showAnimatedArrows: true,
    };
  }

  componentDidMount() {
    this.setLayout();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.setLayout);
    }
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setLayout(
      null,
      _.round(_.get(nextProps, 'data.adjustedOutputs.dti', 0.36) * 100)
    );
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.setLayout);
    }
  }

  onDragStart() {
    const { browser } = this.props;
    if (!browser.isMobile && !browser.isTablet) {
      formOpen({
        formName: FORM_NAME,
        fieldName: 'breakdown-popover',
      });
    }

    this.setState({
      dragging: true,
      showAnimatedArrows: false,
    });
  }

  onDragEnd() {
    const state = {
      dragging: false,
    };

    // Set the new state on drag end.
    this.setState(state);
  }

  onMouseEnter() {
    this.setState({
      breakdownPopoverOpenTime: Date.now(),
    });
  }

  onMouseLeave() {
    if (this.state.breakdownPopoverOpenTime) {
      formCancel({
        formName: FORM_NAME,
        fieldName: 'breakdown-popover',
        fieldValue: Date.now() - this.state.breakdownPopoverOpenTime,
      });

      this.setState({
        breakdownPopoverOpenTime: null,
      });
    }
  }

  getDTI() {
    const { data } = this.props;

    // rc-slider fires an onChange when value lies outside range, supress this behavior
    const wholeNumber = _.round(_.get(data, 'adjustedOutputs.dti', 0.36) * 100);

    return Math.max(
      Math.min(_.isNaN(wholeNumber) ? 36 : wholeNumber, DTI_MAX),
      DTI_MIN
    );
  }

  setLayout(event, value) {
    this.setMessagesPosition(value || this.getDTI());
  }

  setMessagesPosition(value) {
    const width = this.calculateWidth();
    const handlePosition =
      (width / (DTI_MAX - DTI_MIN)) * (Math.min(value, DTI_MAX) - DTI_MIN);

    this.setState({
      handlePosition,
    });
  }

  calculateWidth() {
    if (typeof window !== 'undefined') {
      return (
        this.domNode?.getBoundingClientRect().width -
          this.domNode?.getBoundingClientRect().width / 33 || 0
      );
    }

    return 0;
  }

  handleChange(value) {
    if (value !== this.getDTI()) {
      formInputChanged({
        formName: FORM_NAME,
        fieldName: 'slider-cursor',
        fieldValue: value,
      });

      this.props.onChange(
        {
          key: 'overrides.dti',
        },
        value / 100
      );
    }
  }

  renderAnimatedArrows() {
    const offset = Math.round(this.state.handlePosition - 23);
    if (!this.state.showAnimatedArrows || _.isNaN(offset)) {
      return null;
    }

    return (
      <div className="animated-arrows" style={{ left: `${offset}px` }}>
        <Icon font="gizmo" icon="navigateleft" />
        <div className="animated-arrows-seperator" />
        <Icon font="gizmo" icon="navigateright" />
      </div>
    );
  }

  renderDTIDetails(data) {
    let headerIndex = 2;
    const dti = this.getDTI();

    if (dti <= AFFORDABLE) {
      headerIndex = 0;
    } else if (dti > AFFORDABLE && dti <= STRETCH) {
      headerIndex = 1;
    }

    return (
      <DTIDetails
        browser={this.props.browser}
        data={data}
        config={this.props.DTIDetailsConfig}
        scenarioHeader={splits[headerIndex].scenarioString}
      />
    );
  }

  renderDTISlider(data) {
    const { browser } = this.props;
    const dti = this.getDTI();

    return (
      <div onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <Slider
          dti={dti}
          handleChange={this.handleChange}
          onDragStart={this.onDragStart}
          onDragEnd={this.onDragEnd}
        />
        <div className={!browser.isMobile && !browser.isTablet && 'tooltip'}>
          {this.renderDTIDetails(data)}
        </div>
      </div>
    );
  }

  renderMessageTop(data) {
    const maskStyle = getTranslateCSS(this.state.handlePosition);

    const sliderSections = _.map(splits, (splitConfig) => {
      const { label } = splitConfig;
      const sectionClasses = classnames('dti-slider-section', [
        _.kebabCase(label),
      ]);
      const sectionClassesBar = classnames('dti-slider-section', [
        `${_.kebabCase(label)}-bar`,
      ]);
      const wrapper = classnames(
        sectionClasses,
        'dti-slider-section-wrapper-bar'
      );

      return (
        <div key={`${label}-dti-slider-key`} className={wrapper}>
          <Text size="small">{label}</Text>
          <div className={sectionClassesBar} />
        </div>
      );
    });

    return (
      <div>
        <div
          ref={(el) => {
            this.sliderMessage = el;
          }}
          className="dti-slider-message"
        >
          <Accent size="small">Your monthly payment</Accent>
          <Type size="3" align="left">
            {Num.formatCurrency(
              _.get(data, 'adjustedOutputs.monthlyPayments.total')
            )}
          </Type>
        </div>
        <div className="dti-slider-section-wrapper">
          {sliderSections}
          <div className="dti-slider-section-mask-wrapper">
            <div className="dti-slider-section-mask" style={maskStyle} />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { data } = this.props;
    const debtToIncomeRatioPercent = this.getDTI();

    const classes = classnames('dti-slider', {
      dragging: this.state.dragging,
      affordable: debtToIncomeRatioPercent <= AFFORDABLE,
      stretch:
        debtToIncomeRatioPercent > AFFORDABLE &&
        debtToIncomeRatioPercent <= STRETCH,
      aggressive: debtToIncomeRatioPercent > STRETCH,
    });

    return (
      <div
        ref={(el) => {
          this.domNode = el;
        }}
        className={classes}
        style={{ position: 'relative' }}
      >
        {this.renderMessageTop(data)}
        {this.renderAnimatedArrows()}
        {this.renderDTISlider(data)}
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
DTISlider.displayName = 'DTISlider';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
DTISlider.propTypes = {
  data: PropTypes.object.isRequired,
  DTIDetailsConfig: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  browser: PropTypes.shape({
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
  }).isRequired,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
DTISlider.defaultProps = {
  onChange: () => {},
};

export default DTISlider;
